import { ApolloError, FetchResult, useApolloClient } from '@apollo/client';
import { equals } from 'ramda';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useRouteMatch, withRouter } from 'react-router-dom';
import { Loading } from '../components';
import { ReportSequenceSubscriptionSubscription, ReportSubscriptionSubscription } from '../generated/hooks';
import { reportSequenceQuery } from '../graphql/queries';
import { reportSequenceSubscription } from '../graphql/subscription';
import { Filter } from '../lib/reportHelper';
import auth from '../lib/auth';
import { LOGIN_NEEDED, SOMEONE_ELSE_REPORT } from '../graphql/errors';
import { Error } from './components/Error';
import { useReportSatismeter } from './components/hooks';
import ReportSequence from './sequence/ReportSequencePage';

type ReportSequenceData = NonNullable<NonNullable<ReportSequenceSubscriptionSubscription>['getProcessReport']>;

interface IReportSequenceLoaderProps extends RouteComponentProps<any> {
  accessToken: string;
  selectedLanguage: string;
  changeLanguage: (lang: string) => any;
  setReportRespondent?: (
    respondent?: NonNullable<ReportSubscriptionSubscription['getReport']['metadata']>['respondent'],
  ) => void;
  setAllowedLanguages: (languages: string[]) => void;
  allowedLanguages?: string[];
  setNavbarContent?: (content: React.ReactNode) => void;
}

const ReportSequenceLoader = (props: IReportSequenceLoaderProps) => {
  const { t } = useTranslation('Report');
  const { path } = useRouteMatch();
  const [data, setData] = useState<ReportSequenceData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [reloading, setReloading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const lastSubscription = useRef<any>(null);
  const filtersWereInitialized = useRef<boolean>(false);
  const params = new URLSearchParams(props.location.search);
  const client = useApolloClient();
  const [addedOnFrom, setAddedOnFrom] = useState(
    params.get('added_on_from') === 'null' ? null : params.get('added_on_from') ?? undefined,
  );
  const [addedOnTo, setAddedOnTo] = useState(
    params.get('added_on_to') === 'null' ? null : params.get('added_on_to') ?? undefined,
  );
  const [showOnlyUnread, setShowOnlyUnread] = useState<boolean>(params.get('show_unread') === 'true');
  const [teamsFilter, setTeamsFilter] = useState(params.get('unit-teams')?.split(',') ?? []);
  const [filters, setFilters] = useState<Filter[]>([]);
  const usedTeamsFilter = useRef<string[] | null>(null);
  const [usedAddedOnFrom, setUsedAddedOnFrom] = useState(addedOnFrom);
  const [usedAddedOnTo, setUsedAddedOnTo] = useState(addedOnTo);
  const [previousHiringInfo, setPreviousHiringInfo] = useState(data?.hiringInfo);

  const handleHiringFiltersChange = (newFilters: Filter[]) => {
    setFilters(newFilters);

    const getSelectedIds = (operator: 'position' | 'recruiter' | 'manager') => {
      return (
        newFilters
          .find((filter) => filter.operator === operator)
          ?.values?.filter(Boolean)
          .map((value) => value!.toString()) ?? []
      );
    };

    const positionIds = getSelectedIds('position');
    const recruiterIds = getSelectedIds('recruiter');
    const managerIds = getSelectedIds('manager');

    if (positionIds.length) {
      params.set('position', positionIds.join(','));
    } else {
      params.delete('position');
    }
    if (recruiterIds.length) {
      params.set('recruiter', recruiterIds.join(','));
    } else {
      params.delete('recruiter');
    }
    if (managerIds.length) {
      params.set('manager', managerIds.join(','));
    } else {
      params.delete('manager');
    }

    props.history.push({
      pathname: props.location.pathname,
      search: params.toString(),
    });
  };

  const handleAddedFromChange = (value: string | null) => {
    setAddedOnFrom(value);
    if (value) {
      params.set('added_on_from', value);
    } else {
      params.set('added_on_from', 'null');
    }
    props.history.push({
      pathname: props.location.pathname,
      search: params.toString(),
    });
  };

  const handleAddedToChange = (value: string | null) => {
    setAddedOnTo(value);
    if (value) {
      params.set('added_on_to', value);
    } else {
      params.set('added_on_to', 'null');
    }
    props.history.push({
      pathname: props.location.pathname,
      search: params.toString(),
    });
  };

  const handleShowUnreadResponsesChange = (value: boolean) => {
    setShowOnlyUnread(value);
    params.set('show_unread', value.toString());
    props.history.push({
      pathname: props.location.pathname,
      search: params.toString(),
    });
  };

  const publicAccess = path === '/process-results/:token';

  const loadData = (
    from: string | null | undefined,
    to: string | null | undefined,
    shoudReload?: boolean,
    addedFilters?: Filter[],
  ) => {
    if (
      props.selectedLanguage === data?.metadata?.language?.code &&
      from === data?.addedOnFrom &&
      to === data?.addedOnTo &&
      equals(teamsFilter, usedTeamsFilter.current) &&
      !shoudReload
    ) {
      setUsedAddedOnFrom(from);
      setUsedAddedOnTo(to);
      return;
    }
    if (data) {
      setReloading(true);
    } else {
      setLoading(true);
    }
    setError(null);
    usedTeamsFilter.current = teamsFilter;
    setUsedAddedOnFrom(from);
    setUsedAddedOnTo(to);
    if (lastSubscription.current) {
      lastSubscription.current.unsubscribe();
    }
    const savedCheckedValue = localStorage.getItem(`hideRespondents:${props.accessToken}`);
    const connectedFilters = addedFilters || filters;

    const handleProcessReportLoaded = (
      result: FetchResult<ReportSequenceSubscriptionSubscription>,
      currentFrom: string | null | undefined,
      currentTo: string | null | undefined,
    ) => {
      if (!result?.data) {
        return;
      }
      setData(result.data.getProcessReport);
      setLoading(false);
      setReloading(false);

      props.changeLanguage(result.data.getProcessReport.metadata.language!.code);
      if (!params.get('added_on_from')) {
        handleAddedFromChange(result.data.getProcessReport.addedOnFrom?.toString() ?? null);
      }
      if (!params.get('added_on_to')) {
        handleAddedToChange(result.data.getProcessReport.addedOnTo?.toString() ?? null);
      }
      // Everything is loaded
      if (result.data.getProcessReport.rootTeamsStatistics) {
        setPreviousHiringInfo(result.data.getProcessReport.hiringInfo);
        if (!filtersWereInitialized.current) {
          if (lastSubscription.current) {
            lastSubscription.current.unsubscribe();
          }
          const newFilters: Filter[] = [];
          if (params.get('position')) {
            const positionIds = params.get('position')!.split(',').filter(Boolean);
            newFilters.push({ operator: 'position', values: positionIds, editable: true });
          }
          if (params.get('recruiter')) {
            const recruiterIds = params.get('recruiter')!.split(',').filter(Boolean);
            newFilters.push({ operator: 'recruiter', values: recruiterIds, editable: true });
          }
          if (params.get('manager')) {
            const managerIds = params.get('manager')!.split(',').filter(Boolean);
            newFilters.push({ operator: 'manager', values: managerIds, editable: true });
          }
          setFilters(newFilters);
          filtersWereInitialized.current = true;
          if (newFilters.length) {
            loadData(currentFrom, currentTo, undefined, newFilters);
          }
        }
      }
    };

    lastSubscription.current = client
      .subscribe({
        query: reportSequenceSubscription,
        variables: {
          accessToken: props.accessToken,
          addedOnFrom: from,
          addedOnTo: to,
          languageCode: props.selectedLanguage,
          teamsFilter,
          positionIds: connectedFilters
            .filter((f) => f.operator === 'position')
            .map((f) => f.values)
            .flat(),
          recruiterIds: connectedFilters
            .filter((f) => f.operator === 'recruiter')
            .map((f) => f.values)
            .flat(),
          managerIds: connectedFilters
            .filter((f) => f.operator === 'manager')
            .map((f) => f.values)
            .flat(),
          publicAccess,
          withoutDeleted: savedCheckedValue ? JSON.parse(savedCheckedValue) : false,
          showOnlyUnread,
        },
        fetchPolicy: 'network-only',
      })
      .subscribe({
        next: (result: FetchResult<ReportSequenceSubscriptionSubscription>) => {
          handleProcessReportLoaded(result, from, to);
        },
        error: (err: ApolloError) => {
          if (err.message === LOGIN_NEEDED) {
            auth.redirectToLogin();
            return;
          }
          // try to load report via http query if web socket communication fails - happens a lot in banking sector - stupid IT guys setup stupid firewall rules that block websocket connections
          client
            .query({
              query: reportSequenceQuery,
              variables: {
                accessToken: props.accessToken,
                addedOnFrom: from,
                addedOnTo: to,
                languageCode: props.selectedLanguage,
                teamsFilter,
                positionIds: connectedFilters
                  .filter((f) => f.operator === 'position')
                  .map((f) => f.values)
                  .flat(),
                recruiterIds: connectedFilters
                  .filter((f) => f.operator === 'recruiter')
                  .map((f) => f.values)
                  .flat(),
                managerIds: connectedFilters
                  .filter((f) => f.operator === 'manager')
                  .map((f) => f.values)
                  .flat(),
                publicAccess,
                withoutDeleted: savedCheckedValue ? JSON.parse(savedCheckedValue) : false,
                showOnlyUnread,
              },
              fetchPolicy: 'network-only', // No caching coz the cache might be weird if the subscription fails in a weird way - will cause bugs if removed
            })
            .then((result) => {
              handleProcessReportLoaded(result, from, to);
            })
            .catch((queryErr) => {
              console.error('Query connection error:', queryErr);
              setLoading(false);
              setReloading(false);
              setError(queryErr);
            });
        },
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => loadData(addedOnFrom, addedOnTo), [props.selectedLanguage]);

  useReportSatismeter(data?.metadata, publicAccess);

  const setAddedOnAndLoad = (from: string | null, to: string | null) => {
    handleAddedFromChange(from);
    handleAddedToChange(to);
    loadData(from, to);
  };

  if (!loading && error) {
    return <Error>{t(error.message === SOMEONE_ELSE_REPORT ? 'someoneElseReport' : 'errorLoading')}</Error>;
  }
  if (loading || !data) {
    return <Loading />;
  }

  return (
    <ReportSequence
      {...props}
      reportSequence={data}
      reloading={reloading}
      publicAccess={publicAccess}
      setTeamsFilter={setTeamsFilter}
      teamsFilter={teamsFilter}
      usedTeamsFilter={usedTeamsFilter.current}
      filters={filters}
      setFilters={handleHiringFiltersChange}
      setAddedOnFrom={handleAddedFromChange}
      setAddedOnTo={handleAddedToChange}
      setAddedOnAndLoad={setAddedOnAndLoad}
      addedOnFrom={addedOnFrom}
      addedOnTo={addedOnTo}
      usedAddedOnFrom={usedAddedOnFrom}
      usedAddedOnTo={usedAddedOnTo}
      onConfirm={(shoudReload?: boolean) => loadData(addedOnFrom, addedOnTo, shoudReload)}
      previousHiringInfo={previousHiringInfo}
      setShowOnlyUnread={handleShowUnreadResponsesChange}
      showOnlyUnread={showOnlyUnread}
    />
  );
};

export default withRouter(ReportSequenceLoader);
